/**
 * BI Studio Page — Dashboard Gallery & Viewer
 *
 * Displays saved and template dashboards in a card gallery.
 * Click a dashboard to view it with rendered widgets in a grid layout.
 * Widget types: kpi_card, line_chart, bar_chart, donut, table, gauge.
 * Each widget fetches data from /bi/query.
 *
 * Feature-gated to Enterprise tier.
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Plus,
  ArrowLeft,
  Copy,
  Loader2,
  BarChart3,
  LineChart as LineChartIcon,
  PieChart,
  Table2,
  Gauge,
  Activity,
  Lock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { IC_CHART_COLORS, IC_CHART_TOOLTIP_STYLE } from '@/config/charts';

// ─── Types ──────────────────────────────────────────────────────

interface BIDashboard {
  id: string;
  name: string;
  description: string;
  is_template: boolean;
  widget_count: number;
  created_at: string;
  updated_at: string;
}

interface BIWidget {
  id: string;
  widget_type: 'kpi_card' | 'line_chart' | 'bar_chart' | 'donut' | 'table' | 'gauge';
  title: string;
  config: Record<string, unknown>;
  position: { x: number; y: number; w: number; h: number };
}

interface BIDashboardDetail {
  id: string;
  name: string;
  description: string;
  widgets: BIWidget[];
}

interface WidgetQueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  summary?: Record<string, unknown>;
}

// ─── Data Fetching ──────────────────────────────────────────────

function useDashboards() {
  return useQuery<BIDashboard[]>({
    queryKey: ['ic', 'bi', 'dashboards'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/bi/dashboards');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 60_000,
  });
}

function useDashboardDetail(id: string | null) {
  return useQuery<BIDashboardDetail>({
    queryKey: ['ic', 'bi', 'dashboard', id],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/bi/dashboards/${id}`);
      return res.data?.data ?? res.data;
    },
    enabled: id !== null,
    staleTime: 60_000,
  });
}

function useWidgetData(widgetId: string, config: Record<string, unknown>, enabled: boolean) {
  return useQuery<WidgetQueryResult>({
    queryKey: ['ic', 'bi', 'query', widgetId],
    queryFn: async () => {
      const res = await apiClient.post('/api/v1/impact-core/bi/query', {
        widget_id: widgetId,
        ...config,
      });
      return res.data?.data ?? res.data;
    },
    enabled,
    staleTime: 120_000,
  });
}

// ─── Constants ──────────────────────────────────────────────────

const CHART_COLORS = IC_CHART_COLORS;

// ─── Main Component ─────────────────────────────────────────────

export function BIStudioPage() {
  const { user } = useAuth();
  const tier = user?.subscription_tier ?? 'free';
  const isEnterprise = tier === 'enterprise';

  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  if (!isEnterprise) {
    return <FeatureGate />;
  }

  if (selectedDashboardId) {
    return (
      <DashboardViewer
        dashboardId={selectedDashboardId}
        onBack={() => setSelectedDashboardId(null)}
      />
    );
  }

  return (
    <DashboardGallery
      onSelect={setSelectedDashboardId}
      showCreateForm={showCreateForm}
      onToggleCreate={() => setShowCreateForm((v) => !v)}
    />
  );
}

// ─── Feature Gate ───────────────────────────────────────────────

function FeatureGate() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ic-accent/10">
          <Lock className="h-6 w-6 text-ic-accent" />
        </div>
        <h2 className="text-lg font-semibold text-ic-text-primary">BI Studio</h2>
        <p className="mt-2 text-sm text-ic-text-tertiary">
          BI Studio is available on the Enterprise tier. Upgrade your subscription to build
          custom dashboards with advanced analytics.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-ic-border bg-ic-card px-3 py-2 text-xs font-medium text-ic-text-tertiary">
          <Lock className="h-3.5 w-3.5" />
          Enterprise Feature
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard Gallery ──────────────────────────────────────────

interface GalleryProps {
  onSelect: (id: string) => void;
  showCreateForm: boolean;
  onToggleCreate: () => void;
}

function DashboardGallery({ onSelect, showCreateForm, onToggleCreate }: GalleryProps) {
  const dashboardsQuery = useDashboards();
  const queryClient = useQueryClient();
  const dashboards = dashboardsQuery.data ?? [];

  const saved = dashboards.filter((d) => !d.is_template);
  const templates = dashboards.filter((d) => d.is_template);

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; description: string }) => {
      const res = await apiClient.post('/api/v1/impact-core/bi/dashboards', { ...payload });
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'bi', 'dashboards'] });
      onToggleCreate();
    },
  });

  const cloneMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const res = await apiClient.post(`/api/v1/impact-core/bi/dashboards/${templateId}/clone`);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'bi', 'dashboards'] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">BI Studio</h1>
          <p className="mt-0.5 text-xs text-ic-text-tertiary">
            Build and view custom dashboards for your portfolio
          </p>
        </div>
        <button
          onClick={onToggleCreate}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent/90 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Dashboard
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <CreateDashboardForm
          onSubmit={(name, description) => createMutation.mutate({ name, description })}
          onCancel={onToggleCreate}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Saved Dashboards */}
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          My Dashboards
        </h2>
        {dashboardsQuery.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 animate-pulse rounded-lg bg-slate-700/50" />
            ))}
          </div>
        ) : saved.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-ic-border">
            <p className="text-xs text-ic-text-tertiary">
              No dashboards yet. Create one or clone a template.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {saved.map((d) => (
              <DashboardCard key={d.id} dashboard={d} onClick={() => onSelect(d.id)} />
            ))}
          </div>
        )}
      </section>

      {/* Templates */}
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          Templates
        </h2>
        {templates.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-ic-border">
            <p className="text-xs text-ic-text-tertiary">No templates available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <DashboardCard
                key={t.id}
                dashboard={t}
                onClick={() => onSelect(t.id)}
                onClone={() => cloneMutation.mutate(t.id)}
                isCloning={cloneMutation.isPending}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Dashboard Card ─────────────────────────────────────────────

interface DashboardCardProps {
  dashboard: BIDashboard;
  onClick: () => void;
  onClone?: () => void;
  isCloning?: boolean;
}

function DashboardCard({ dashboard, onClick, onClone, isCloning }: DashboardCardProps) {
  return (
    <div
      className="group cursor-pointer rounded-lg border border-ic-border bg-ic-card p-4 hover:border-ic-accent/50 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ic-accent/10">
          <LayoutDashboard className="h-4 w-4 text-ic-accent" />
        </div>
        {onClone && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClone();
            }}
            disabled={isCloning}
            className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-ic-text-tertiary hover:bg-ic-hover hover:text-ic-text-secondary transition-colors"
          >
            {isCloning ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
            Clone
          </button>
        )}
      </div>
      <h3 className="mt-3 text-sm font-medium text-ic-text-primary group-hover:text-ic-accent transition-colors">
        {dashboard.name}
      </h3>
      <p className="mt-1 line-clamp-2 text-[11px] text-ic-text-tertiary">
        {dashboard.description}
      </p>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-ic-text-tertiary">
        <span>{dashboard.widget_count} widgets</span>
        {dashboard.is_template && (
          <span className="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-blue-400">
            Template
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Create Dashboard Form ──────────────────────────────────────

interface CreateFormProps {
  onSubmit: (name: string, description: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CreateDashboardForm({ onSubmit, onCancel, isLoading }: CreateFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (name.trim()) {
        onSubmit(name.trim(), description.trim());
      }
    },
    [name, description, onSubmit],
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-ic-accent/30 bg-ic-card p-4 space-y-3"
    >
      <div>
        <label className="block text-xs font-medium text-ic-text-secondary mb-1">
          Dashboard Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Portfolio ESG Overview"
          className="w-full rounded-md border border-ic-border bg-ic-surface px-3 py-2 text-sm text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:border-ic-accent focus:outline-none"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-ic-text-secondary mb-1">
          Description
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this dashboard"
          className="w-full rounded-md border border-ic-border bg-ic-surface px-3 py-2 text-sm text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:border-ic-accent focus:outline-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!name.trim() || isLoading}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-ic-accent/90 disabled:opacity-50 transition-colors"
        >
          {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-xs text-ic-text-tertiary hover:text-ic-text-secondary transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Dashboard Viewer ───────────────────────────────────────────

interface ViewerProps {
  dashboardId: string;
  onBack: () => void;
}

function DashboardViewer({ dashboardId, onBack }: ViewerProps) {
  const detailQuery = useDashboardDetail(dashboardId);
  const dashboard = detailQuery.data;

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-700/50" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-lg bg-slate-700/50" />
          ))}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-ic-text-tertiary">Dashboard not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-ic-border hover:bg-ic-hover transition-colors"
        >
          <ArrowLeft className="h-4 w-4 text-ic-text-secondary" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">{dashboard.name}</h1>
          <p className="text-xs text-ic-text-tertiary">{dashboard.description}</p>
        </div>
      </div>

      {/* Widget Grid */}
      {dashboard.widgets.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-ic-border">
          <p className="text-xs text-ic-text-tertiary">
            This dashboard has no widgets yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {dashboard.widgets.map((widget) => (
            <WidgetRenderer key={widget.id} widget={widget} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Widget Renderer ────────────────────────────────────────────

function WidgetRenderer({ widget }: { widget: BIWidget }) {
  const dataQuery = useWidgetData(widget.id, widget.config, true);

  const widgetIcon = getWidgetIcon(widget.widget_type);

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="mb-3 flex items-center gap-2">
        {widgetIcon}
        <h4 className="text-xs font-medium text-ic-text-secondary">{widget.title}</h4>
      </div>

      {dataQuery.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
        </div>
      ) : dataQuery.isError ? (
        <div className="flex h-32 items-center justify-center">
          <p className="text-[10px] text-red-400">Failed to load widget data</p>
        </div>
      ) : (
        <WidgetContent type={widget.widget_type} data={dataQuery.data ?? null} />
      )}
    </div>
  );
}

function getWidgetIcon(type: BIWidget['widget_type']) {
  switch (type) {
    case 'kpi_card':
      return <Activity className="h-3.5 w-3.5 text-ic-accent" />;
    case 'line_chart':
      return <LineChartIcon className="h-3.5 w-3.5 text-blue-400" />;
    case 'bar_chart':
      return <BarChart3 className="h-3.5 w-3.5 text-emerald-400" />;
    case 'donut':
      return <PieChart className="h-3.5 w-3.5 text-purple-400" />;
    case 'table':
      return <Table2 className="h-3.5 w-3.5 text-cyan-400" />;
    case 'gauge':
      return <Gauge className="h-3.5 w-3.5 text-amber-400" />;
  }
}

// ─── Widget Content by Type ─────────────────────────────────────

interface WidgetContentProps {
  type: BIWidget['widget_type'];
  data: WidgetQueryResult | null;
}

function WidgetContent({ type, data }: WidgetContentProps) {
  if (!data || data.rows.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-[10px] text-ic-text-tertiary">No data</p>
      </div>
    );
  }

  switch (type) {
    case 'kpi_card':
      return <KPIWidget data={data} />;
    case 'line_chart':
      return <LineChartWidget data={data} />;
    case 'bar_chart':
      return <BarChartWidget data={data} />;
    case 'donut':
      return <DonutWidget data={data} />;
    case 'table':
      return <TableWidget data={data} />;
    case 'gauge':
      return <GaugeWidget data={data} />;
  }
}

function KPIWidget({ data }: { data: WidgetQueryResult }) {
  const firstRow = data.rows[0];
  const valueKey = data.columns[0] ?? 'value';
  const labelKey = data.columns[1] ?? 'label';
  const value = firstRow ? String(firstRow[valueKey] ?? '--') : '--';
  const label = firstRow ? String(firstRow[labelKey] ?? '') : '';

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <p className="text-2xl font-bold text-ic-text-primary tracking-tight">{value}</p>
      {label && <p className="mt-1 text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>}
    </div>
  );
}

function LineChartWidget({ data }: { data: WidgetQueryResult }) {
  const xKey = data.columns[0] ?? 'x';
  const yKey = data.columns[1] ?? 'y';

  return (
    <div className="h-36">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.rows}>
          <XAxis dataKey={xKey} tick={{ fontSize: 9, fill: '#64748B' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#64748B' }} tickLine={false} axisLine={false} width={40} />
          <Tooltip contentStyle={tooltipStyle} />
          <Line type="monotone" dataKey={yKey} stroke="#3B82F6" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BarChartWidget({ data }: { data: WidgetQueryResult }) {
  const xKey = data.columns[0] ?? 'x';
  const yKey = data.columns[1] ?? 'y';

  return (
    <div className="h-36">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.rows}>
          <XAxis dataKey={xKey} tick={{ fontSize: 9, fill: '#64748B' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#64748B' }} tickLine={false} axisLine={false} width={40} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey={yKey} radius={[4, 4, 0, 0]} barSize={20}>
            {data.rows.map((_row, idx) => (
              <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length] ?? '#64748B'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DonutWidget({ data }: { data: WidgetQueryResult }) {
  const nameKey = data.columns[0] ?? 'name';
  const valueKey = data.columns[1] ?? 'value';

  return (
    <div className="h-36">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie
            data={data.rows}
            dataKey={valueKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius="50%"
            outerRadius="80%"
            paddingAngle={2}
            strokeWidth={0}
          >
            {data.rows.map((_row, idx) => (
              <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length] ?? '#64748B'} />
            ))}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
        </RechartsPie>
      </ResponsiveContainer>
    </div>
  );
}

function TableWidget({ data }: { data: WidgetQueryResult }) {
  return (
    <div className="max-h-48 overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-ic-border text-left">
            {data.columns.map((col) => (
              <th key={col} className="pb-2 pr-3 text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-b border-ic-border/50 last:border-0">
              {data.columns.map((col) => (
                <td key={col} className="py-1.5 pr-3 text-ic-text-secondary">
                  {String(row[col] ?? '--')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GaugeWidget({ data }: { data: WidgetQueryResult }) {
  const firstRow = data.rows[0];
  const valueKey = data.columns[0] ?? 'value';
  const rawValue = firstRow ? Number(firstRow[valueKey] ?? 0) : 0;
  const value = Math.max(0, Math.min(100, rawValue));

  const barColor =
    value >= 80 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="flex flex-col items-center justify-center py-4 gap-3">
      <p className="text-2xl font-bold text-ic-text-primary">{value}%</p>
      <div className="h-2 w-full max-w-[200px] overflow-hidden rounded-full bg-slate-700/50">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${barColor}`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

const tooltipStyle = IC_CHART_TOOLTIP_STYLE;
